import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { m as motion } from "framer-motion";
import { fadeInUp, defaultViewport, defaultTransition } from "@/lib/motion";
import { EXTERNAL_LINKS } from "../lib/constants";

export const FAQSection = () => {
  const faqItems = [
    {
      question: "Who is The AI for HR Mastermind for?",
      answer: (
        <>
          <p>
            This mastermind is for HR and People leaders who want to demystify
            Gen AI, spot use cases aligned with business goals, and turn their
            teams into the go-to company experts on AI. It's ideal for:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>HR professionals looking to start or streamline their AI journey</li>
            <li>Leaders who want to network with other early adopters in HR</li>
            <li>Those seeking to save costs by learning how to avoid common AI implementation mistakes</li>
            <li>HR executives aiming to play more strategic roles in their organizations</li>
            <li>Professionals interested in implementing evidence-based talent practices using AI</li>
            <li>HR leaders ready for a hybrid digital and in-person experience for optimal learning and engagement</li>
          </ul>
          <p className="mt-2">
            <strong>
              Curious if The AI for HR Mastermind is for you?{" "}
              <a
                href={EXTERNAL_LINKS.PREMIUM_MASTERMIND_APPLICATION}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Apply and we will let you know!
              </a>
            </strong>
          </p>
        </>
      ),
    },
    {
      question: "What does the 5-session program cover?",
      answer: (
        <>
          <p>Our comprehensive 5-session program covers:</p>
          <ol className="list-decimal list-inside space-y-2 mt-2">
            <li><strong>Session 1: Understanding your AI Readiness</strong> - The six forces that shape whether AI deployments stick — or stall.</li>
            <li><strong>Session 2: Selecting a Use Case with Task-Tech Fit</strong> - Five practical filters for picking work where AI earns trust and ROI.</li>
            <li><strong>Session 3: Creating an AI Assistant Proof-of-Concept</strong> - Define the job, constrain the behavior, test with real scenarios.</li>
            <li><strong>Session 4: Preparing Knowledge for AI</strong> - Extract, clean, and structure what the model can use — so answers are useful.</li>
            <li><strong>Session 5: Preparing to Scale</strong> - Turn a working demo into adoption with a concrete rollout plan.</li>
          </ol>
        </>
      ),
    },
    {
      question: "What if I can't make a session?",
      answer: (
        <p>
          All calls are recorded for you to watch later if you're unable to make
          the live sessions. Our in-person sessions will also be recorded and
          available on your private community page.
        </p>
      ),
    },
    {
      question: "What is your refund policy?",
      answer: (
        <p>
          We have a no-questions-asked refund policy. If you ever feel that the
          program isn't meeting your expectations or isn't a good fit for you,
          we'll refund your full tuition. Our goal is to ensure you receive value
          from the program, and we stand behind its quality.
        </p>
      ),
    },
    {
      question: "What is included in the program?",
      answer: (
        <>
          <p>The AI for HR Mastermind includes:</p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>Weekly interactive and hands-on group training sessions</li>
            <li>Resources curated specifically for HR leaders</li>
            <li>Reusable templates and frameworks for AI implementation</li>
            <li>Networking opportunities with like-minded HR and People leaders</li>
            <li>Access to a private community page for ongoing discussions and resource sharing</li>
            <li>Recordings of all sessions for review</li>
          </ul>
        </>
      ),
    },
    {
      question: "How can this mastermind benefit my organization?",
      answer: (
        <>
          <p>
            This mastermind will support you confidently leading AI initiatives in
            your HR department, potentially reducing costs, improving efficiency,
            and enhancing decision-making. You'll learn to identify valuable AI use
            cases, build prototypes, and create action plans for implementation.
          </p>
          <p>
            We believe there is a huge opportunity for HR teams to drive the
            healthy adoption of AI in their organizations.
          </p>
        </>
      ),
    },
    {
      question: "What happens when I purchase standard and want to upgrade to premium?",
      answer: (
        <p>
          The cost for premium will be pro-rated based on how much of your standard year membership is left. This ensures you only pay for the additional value you're receiving during the remainder of your membership period.
        </p>
      ),
    },
  ];

  return (
    <section className="w-full relative overflow-hidden">
      {/* Hero */}
      <div className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-mesh opacity-30 pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/3 left-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="section-inner relative z-10">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={defaultViewport}
            transition={defaultTransition}
            className="flex flex-col items-center text-center"
          >
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))]">
              Frequently Asked Questions
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mt-4">
              Find answers to common questions about The AI for HR Mastermind
            </p>
          </motion.div>
        </div>
      </div>

      {/* FAQ Accordion */}
      <div className="section-inner pb-20">
        <Accordion
          type="single"
          collapsible
          className="w-full max-w-3xl mx-auto space-y-4"
        >
          {faqItems.map((item, index) => (
            <motion.div
              key={index}
              initial={fadeInUp.initial}
              whileInView={fadeInUp.animate}
              viewport={defaultViewport}
              transition={{ ...defaultTransition, delay: index * 0.05 }}
            >
              <AccordionItem
                value={`item-${index + 1}`}
                className="glass-card px-6 border border-border/30 rounded-xl overflow-hidden"
              >
                <AccordionTrigger className="text-lg md:text-xl font-semibold py-5 hover:text-primary transition-colors text-left">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-5">
                  <div className="text-[17px] space-y-2 text-foreground/80 leading-relaxed">
                    {item.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
